import React, { useState } from 'react';
import apiClient from '../../../services/api';
import ScalpingFilterPanel from './ScalpingFilterPanel';
import ScalpingResultsTable from './ScalpingResultsTable';

/**
 * Options Scalper.
 *
 * Hunts individual option contracts trading at a discount to their OWN prior
 * close. The thesis is contract-level, not chart-level: an IV crush, a gap, or
 * a bid vanishing on a thin strike can mark a contract down hard without the
 * underlying doing anything interesting.
 *
 * Self-contained like ExplorerTab: owns its data/loading/error state and its
 * own ticker input, so it works without first running an analysis.
 */
function ScalpingTab({ onAddToWatchlist, watchedStocks }) {
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);

  const handleScan = async (payload) => {
    setLoading(true);
    setError(null);
    setLoadingMessage(
      `Fetching options chains for ${payload.tickers.join(', ')}...`
    );

    try {
      const response = await apiClient.post('/stock-analyzer/scalping/', payload);
      setScanData(response.data);
    } catch (err) {
      console.error('Scalping scan failed:', err);
      const details = err.response?.data?.details;
      if (details && typeof details === 'object') {
        // Surface per-field validation messages rather than a generic failure.
        const fieldErrors = Object.entries(details)
          .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          )
          .join(' • ');
        setError(fieldErrors);
      } else {
        setError(err.response?.data?.error || 'Failed to run scalping scan');
      }
      setScanData(null);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const results = scanData?.results || [];
  const scanErrors = scanData?.errors || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Options Scalper</h2>
        <p className="text-gray-400 text-sm">
          Find option contracts marked down hard since yesterday&apos;s close — the kind of
          dislocation that comes from an IV crush, a gap, or a bid vanishing on a thin
          strike. Cheap enough to size into, with enough time left for the thesis to work.
        </p>
      </div>

      <ScalpingFilterPanel onScan={handleScan} loading={loading} />

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">{loadingMessage}</p>
          </div>
        </div>
      )}

      {/* Request-level error */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {scanData && !loading && (
        <>
          {/* Partial failures are shown ALONGSIDE the results -- one bad ticker
              must never discard an otherwise good scan. */}
          {scanErrors.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-4">
              <h3 className="text-yellow-400 font-medium mb-2 text-sm">
                Some tickers could not be scanned:
              </h3>
              <ul className="text-yellow-300 text-xs space-y-1">
                {scanErrors.map((e, i) => (
                  <li key={i}>• <strong>{e.ticker}</strong>: {e.error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-gray-700 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-white">Scan Results</h3>
              <span className="px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm">
                {scanData.total_found} contract{scanData.total_found === 1 ? '' : 's'} matched
              </span>
            </div>

            <ScalpingResultsTable
              data={results}
              totalFound={scanData.total_found}
              truncated={scanData.truncated}
              onAddToWatchlist={onAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        </>
      )}

      {/* Empty state before the first scan */}
      {!scanData && !loading && !error && (
        <div className="text-center py-12 border-t border-gray-700">
          <div className="text-5xl mb-3">🎯</div>
          <p className="text-gray-400">
            Pick your tickers and a scan mode, then run a scan.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Your filter set is remembered between visits.
          </p>
        </div>
      )}
    </div>
  );
}

export default ScalpingTab;
